var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ReshapeV2Config_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { AddMode } from './enums';
import { reshapeV2ConfigSchemaValidation } from '../validations.generated';
export const reshapeV2ConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:ReshapeV2Config',
    type: 'object',
    properties: {
        add: { type: 'string', enum: Object.values(AddMode) },
        wideColumns: {
            type: 'array',
            items: {
                oneOf: [
                    {
                        $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                    },
                    { type: 'string', minLength: 1, maxLength: 150 }
                ]
            },
            uniqueItems: true,
            maxItems: 1000
        },
        longColumn: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        },
        valueColumn: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        }
    },
    required: ['add', 'wideColumns', 'longColumn', 'valueColumn']
};
let ReshapeV2Config = ReshapeV2Config_1 = class ReshapeV2Config extends ToolConfig {
    constructor(add, wideColumns, longColumn, valueColumn, customFormats) {
        super(ToolType.RESHAPEV2, customFormats);
        // This tool changes data from "wide form" to "long form" and vice versa.
        //
        // Wide Form Example:
        //
        // ID Jan Feb Mar
        //  1  10  20  30
        //  2   1   2   3
        //  3  15  50  35
        //
        // To convert to Long Form: add=rows, wideColumns=[Jan,Feb,Mar], longColumn='Months', valueColumn='value'
        //
        // ID Months Value
        //  1    Jan    10
        //  2    Feb    20
        //  3    Mar    30
        //  1    Jan     1
        //  2    Feb     2
        //  3    Mar     3
        //  1    Jan    15
        //  2    Feb    50
        //  3    Mar    35
        //
        // To convert back to Wide Form: add=columns, wideColumns=[Jan,Feb,Mar], longColumn='Months', valueColumn='value'
        this.add = add;
        this.wideColumns = wideColumns;
        this.longColumn = longColumn; // default: Label
        this.valueColumn = valueColumn; // default: Value
        JSON.validate(this, ReshapeV2Config_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return ReshapeV2Config_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return ReshapeV2Config_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return ReshapeV2Config_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return ReshapeV2Config_1.outs;
    }
    static get in() {
        return ReshapeV2Config_1.ins[0];
    }
    static get out() {
        return ReshapeV2Config_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.RESHAPE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.ORGANIZE], this.ins, this.outs, ['reshapeV2', 'pivot', 'wide form', 'long form', 'transpose'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new ReshapeV2Config_1(baseVals.add, baseVals.wideColumns, baseVals.longColumn, baseVals.valueColumn, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            add: this.add,
            wideColumns: parameters.getStrArrayVals(this.wideColumns),
            longColumn: parameters.getStrVal(this.longColumn),
            valueColumn: parameters.getStrVal(this.valueColumn)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const baseVals = this.getBaseValues(iConfigHelper.getParameters());
            const exp = tsb.export(this.out);
            const idColumns = ReshapeV2Config_1.identityColumns(baseVals, source);
            if (baseVals.add === AddMode.rows) {
                // wide form to long form
                // add all identity columns
                idColumns.forEach((s) => exp.copy(this.in, s, true, true));
                // dependents would be id cols + wide cols (all cols?)
                const dependents = [...idColumns];
                // value column type matches wide columns if all wide columns are the same type otherwise
                // it is string.
                const types = new Set();
                for (const wide of baseVals.wideColumns) {
                    const column = source.find((c) => c.name === wide);
                    types.add(column.type);
                    dependents.push(column);
                }
                let valueType = types.size === 1 ? types.values().next().value : 'string';
                if (valueType === undefined) {
                    valueType = 'string';
                }
                // add new long form columns
                exp.makeNew(new Column(baseVals.longColumn, 'string'), dependents, true);
                exp.makeNew(new Column(baseVals.valueColumn, valueType), dependents, true);
                // implicitly remove wide columns by not including them
            }
            else {
                // long form to wide form
                // add all identity columns
                idColumns.forEach((s) => exp.copy(this.in, s, true, true));
                // wide columns are the same type as value column
                const valueColumn = source.find((c) => c.name === baseVals.valueColumn);
                // dependents would be id cols, value col, long col
                const dependents = [
                    ...idColumns,
                    exp.findSourceColumn(this.in, baseVals.longColumn),
                    valueColumn
                ];
                // add new wide form columns
                const newColumns = baseVals.wideColumns.map((c) => new Column(c, valueColumn.type));
                newColumns.forEach((c) => exp.makeNew(c, dependents, true));
                // implicitly remove long columns by not including them
            }
            exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source)
            return ToolValidationState.BAD_METADATA;
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        const idColumns = ReshapeV2Config_1.identityColumns(baseVals, source);
        const idNames = new Set(idColumns.map((c) => c.name));
        if (baseVals.add === AddMode.rows) {
            // wide form to long form
            // All wide columns must exist in input. All types are valid. We'll convert value column to string
            // if needed.
            for (const wide of baseVals.wideColumns) {
                if (!source.find((c) => c.name === wide))
                    return ToolValidationState.BAD_METADATA;
            }
            // { ids, longColumn, valueColumn } must be unique
            if (idNames.has(baseVals.longColumn))
                return ToolValidationState.BAD_METADATA;
            if (idNames.has(baseVals.valueColumn))
                return ToolValidationState.BAD_METADATA;
        }
        else {
            // long form to wide form
            // long column and value column must exist in input
            const required = source.filter((c) => c.name === baseVals.longColumn || c.name === baseVals.valueColumn);
            if (required.length !== 2)
                return ToolValidationState.BAD_METADATA;
            // We'll convert the longColumn to a string so longColumns of all types are valid.
            // All types are valid for value column. All wide columns will be of whatever type
            // it is. Wide columns that don't appear as values in longColumn are fine as well, we
            // will add them as empty columns.
            const duplicates = baseVals.wideColumns.filter((c) => idNames.has(c));
            if (duplicates.length > 0)
                return ToolValidationState.BAD_METADATA;
        }
        return ToolValidationState.VALID;
    }
    static identityColumns(baseVals, source) {
        if (baseVals.add === AddMode.rows) {
            // wide form to long form -> all non-wide columns
            const wides = new Set(baseVals.wideColumns);
            const idColumns = source.filter((c) => !wides.has(c.name));
            return idColumns;
        }
        else {
            // long form to wide form -> all non-long columns
            const longs = new Set([baseVals.longColumn, baseVals.valueColumn]);
            const idColumns = source.filter((c) => !longs.has(c.name));
            return idColumns;
        }
    }
};
ReshapeV2Config = ReshapeV2Config_1 = __decorate([
    JsonSchemaV2(reshapeV2ConfigSchema, reshapeV2ConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.RESHAPEV2)
], ReshapeV2Config);
export { ReshapeV2Config };
//# sourceMappingURL=reshapeV2Config.js.map